import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

interface LoyaltyCommissionData {
  commissionPercentage: number
  description?: string
  validFrom?: string
  validUntil?: string
}

// Interface pour la réponse API
interface ApiResponse<T = any> {
  success: boolean
  message?: string
  data?: T
  commission?: T
  users?: T
  stats?: T
}

// Service unifié pour la gestion des commissions
export const loyaltyCommissionService = {

  /* Récupérer la commission active d'un utilisateur */
  getUserActiveCommission: async (userId: number): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      const response = await axios.get(`${API_URL}/loyalty-commission/active-commission/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la récupération de la commission active de l'utilisateur:", error)
      return {
        success: false,
        commission: null,
        message: error.response?.data?.message || "Erreur lors de la récupération de la commission active",
      }
    }
  },

  /* Récupérer la commission (la plus récente) d'un utilisateur */
  getUserCommission: async (userId: number): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      const response = await axios.get(`${API_URL}/loyalty-commission/user-id/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la récupération de la commission de l'utilisateur:", error)
      return {
        success: false,
        commission: null,
        message: error.response?.data?.message || "Erreur lors de la récupération de la commission",
      }
    }
  },

  /* Créer ou mettre à jour la commission d'un utilisateur */
  createOrUpdateUserCommission: async (userId: number, data: LoyaltyCommissionData): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      // Validation des données côté client
      if (data.commissionPercentage < 0 || data.commissionPercentage > 100) {
        throw new Error("Le pourcentage doit être entre 0 et 100")
      }

      if (data.validFrom && data.validUntil && new Date(data.validFrom) > new Date(data.validUntil)) {
        throw new Error("La date de début doit être antérieure à la date de fin")
      }

      const response = await axios.post(`${API_URL}/loyalty-commission/users/${userId}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la création/mise à jour de la commission utilisateur:", error)
      throw error
    }
  },

  /* Activer/désactiver la commission d'un utilisateur */
  toggleUserCommissionStatus: async (userId: number, commissionId: number): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      const response = await axios.put(
        `${API_URL}/loyalty-commission/users/${userId}/${commissionId}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )
      return response.data
    } catch (error: any) {
      console.error("Erreur lors du changement de statut de la commission utilisateur:", error)
      throw error
    }
  },

  /* Supprimer la commission d'un utilisateur */
  deleteUserCommission: async (userId: number, commissionId: number): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      const response = await axios.delete(`${API_URL}/loyalty-commission/users/${userId}/${commissionId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la suppression de la commission utilisateur:", error)
      throw error
    }
  },

  /* Récupérer toutes les commissions des utilisateurs (pour admin) */
  getAllUsersCommissions: async (): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }
      const response = await axios.get(`${API_URL}/loyalty-commission/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la récupération des commissions utilisateurs (admin):", error)
      return {
        success: false,
        users: [],
        message: error.response?.data?.message || "Erreur lors de la récupération des commissions",
      }
    }
  },

  /* Appliquer une commission à tous les utilisateurs (pour admin) */
  applyCommissionToAllUsers: async (data: LoyaltyCommissionData): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      // Validation des données côté client
      if (data.commissionPercentage < 0 || data.commissionPercentage > 100) {
        throw new Error("Le pourcentage doit être entre 0 et 100")
      }

      if (data.validFrom && data.validUntil && new Date(data.validFrom) > new Date(data.validUntil)) {
        throw new Error("La date de début doit être antérieure à la date de fin")
      }

      const response = await axios.post(`${API_URL}/loyalty-commission/admin/apply-to-all`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de l'application de la commission à tous les utilisateurs:", error)
      throw error
    }
  },

  /* Désactiver toutes les commissions (pour admin) */
  disableAllCommissions: async (): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }
      const response = await axios.post(
        `${API_URL}/loyalty-commission/admin/disable-all`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la désactivation globale des commissions:", error)
      throw error
    }
  },

  /* Récupérer les statistiques des commissions (pour admin) */
  getCommissionStats: async (): Promise<ApiResponse> => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Token d'authentification manquant")
      }

      const response = await axios.get(`${API_URL}/loyalty-commission/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data
    } catch (error: any) {
      console.error("Erreur lors de la récupération des statistiques:", error)
      return {
        success: false,
        stats: null,
        message: error.response?.data?.message || "Erreur lors de la récupération des statistiques",
      }
    }
  },
}

export default loyaltyCommissionService
